import { css } from '@emotion/react';
import { alpha } from '@mui/material/styles';

const makeStoreMapStyles = ({ theme }) => {
  return {
    map: css({
      position: 'relative',
      width: '100%',
      height: 280,
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      borderRadius: '6px',
      marginBottom: 10,
      // Shrink the map on short viewports so the Continue button and at least
      // one store card stay visible.
      '@media (max-height: 760px)': { height: 170 },
    }),
    messageContainer: css({
      maxWidth: 640,
      margin: '20px auto',
      textAlign: 'center',
    }),
    legend: css({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      borderRadius: '20px',
      padding: '4px 12px',
      marginBottom: 12,
    }),
    legendMarker: css({
      width: 10,
      height: 10,
      borderRadius: '50%',
      // Matches Google's default marker colour (the map pins).
      backgroundColor: '#ea4335',
    }),
    // StoreMap is a flex column bounded by a JS-computed max-height (see
    // useOlbScrollMaxHeight): fixed map header + scrollable cards + Continue.
    root: css({
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }),
    // The map + legend are a fixed header (always visible); the cards scroll.
    mapHeader: css({
      flexShrink: 0,
      paddingBottom: 6,
    }),
    // The store cards take the remaining height and scroll (invisible scrollbar).
    cardScrollArea: css({
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }),
    storeCard: css({
      display: 'block',
      width: '100%',
      textAlign: 'left',
      textTransform: 'none',
      backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      borderRadius: '6px',
      padding: 15,
      marginBottom: 10,
      '&:hover': {
        backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
      },
    }),
    storeCardSelected: css({
      borderColor: theme.mvf.palette.component.answerSelectedColor,
      borderWidth: theme.mvf.border.width.medium,
    }),
    // Number sits top-left; the name + address share the body column to its
    // right, so they left-align on the same x (the address pin marks that x).
    storeCardRow: css({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }),
    storeNumber: css({
      flexShrink: 0,
      width: 24,
      height: 24,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.mvf.palette.component.answerSelectedColor,
      '& p': {
        color: theme.mvf.palette.text.continueButtonTextColor,
        fontWeight: theme.mvf.typography.weight.thick,
      },
    }),
    storeCardBody: css({
      flexGrow: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    }),
    storeCardHeader: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
    }),
    storeName: css({
      flexGrow: 1,
      minWidth: 0,
      textAlign: 'left',
      '& p': {
        textAlign: 'left',
        fontWeight: theme.mvf.typography.weight.thick,
      },
    }),
    storeDistance: css({
      flexShrink: 0,
      backgroundColor: alpha(
        theme.mvf.palette.component.answerSelectedColor,
        0.15
      ),
      borderRadius: '12px',
      padding: '2px 10px',
      '& p': {
        color: theme.mvf.palette.component.answerSelectedColor,
        fontWeight: theme.mvf.typography.weight.thick,
      },
    }),
    storeAddress: css({
      margin: '4px 0 0 0',
      textAlign: 'left',
      '& p': {
        textAlign: 'left',
        color: theme.mvf.palette.text.secondaryTextColor,
      },
    }),
    addressPin: css({
      width: 12,
      height: 12,
      marginRight: 4,
      verticalAlign: 'middle',
    }),
    // Pinned footer: the Continue button stays visible while the list scrolls.
    continueContainer: css({
      flexShrink: 0,
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      marginTop: 5,
      paddingTop: 10,
      paddingBottom: 4,
      backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
    }),
  };
};

export default makeStoreMapStyles;
